// The Blueprint frame (DEC-008, pages/ux-full DESIGN.md §6): the places across the top, the place's
// pages down the side rail by group (the register, rail C), and the screen on its sheet. It draws only
// the places this person's permissions open (Nav.tsx's PLACES); what waits counts on its page and its place.
// Each screen's own Header draws its title block inside the sheet. Under the top bar, the calls strip
// (calls/CallsStripHost.tsx) when a call rings or runs: on every page, above the rail and the sheet.
import { useState, type ReactNode } from 'react';
import { watchScrollEdges } from '@flowology/ui-web/fl';
import { WAITS, waitingOn, waitingWords, type NavProps, type View } from '../Nav.tsx';
import { HelpDialog } from '../shared/Help.tsx';
import { NotificationsBell } from '../shared/Notifications.tsx';
import { forgetSessionCaches } from '../shared/session-caches.ts';
import {
  canOpenIn,
  isOpen,
  pageLabel,
  placeOf,
  placesFor,
  type BlueprintPlace,
  type RailPage,
} from './places.ts';
import { CrumbContext } from './design.ts';
import { HeaderSearch } from './HeaderSearch.tsx';
import { MeMenu } from './MeMenu.tsx';
import { maySearch } from './search-model.ts';
import './shell.css';

const pagesOf = (p: BlueprintPlace): RailPage[] => p.groups.flatMap((g) => g.pages);
/** What a place counts: its rail's pages and those off it (Schedule's To schedule). */
const countedOf = (p: BlueprintPlace): RailPage[] => [...pagesOf(p), ...(p.offRail ?? [])];

// The count the eye reads; a screen reader hears what it counts as the link's description (described),
// never in its name, so a link is called its place or its page ("Inbox", QA-359, QA-673).
function Count({ n, hot }: { n: number; hot: boolean }) {
  if (n <= 0) return null;
  return (
    <span className={`bp-cnt${hot ? ' hot' : ''}`} aria-hidden="true">
      {n}
    </span>
  );
}

export function Frame({
  nav,
  strip,
  children,
}: {
  nav: NavProps;
  /** Under the top bar, on every page: the calls strip. */
  strip?: ReactNode;
  children: ReactNode;
}) {
  const held = nav.permissions ?? [];
  const places = placesFor(canOpenIn(held), held);
  const at = placeOf(nav.view, nav.part);
  const here = places.find((p) => p.id === at.id) ?? places[0];
  const [help, setHelp] = useState(false);
  const waits = (pages: RailPage[]) => pages.reduce((n, x) => n + waitingOn(nav, x.view, x.part), 0);
  const ringing = (pages: RailPage[]) => nav.ringing > 0 && pages.some((x) => x.view === 'calls');
  const go = (v: View, part?: string) => (e: { preventDefault(): void }) => {
    e.preventDefault();
    nav.onView(v, part);
  };
  // a page's link: its screen's, and its part's where it has one (#/books/banking)
  const href = (x: Pick<RailPage, 'view' | 'part'>) => `#/${x.view}${x.part ? `/${x.part}` : ''}`;
  // what waits on a link's pages, as its description: "1 ringing", "2 to book" (the spans below)
  const waiting = WAITS.filter((v) => waitingOn(nav, v) > 0);
  const described = (pages: Pick<RailPage, 'view'>[]) => {
    const ids = waiting.filter((v) => pages.some((x) => x.view === v)).map((v) => `bp-waiting-${v}`);
    return ids.length ? { 'aria-describedby': ids.join(' ') } : {};
  };

  return (
    <div className="bp-app">
      <header className="bp-top">
        <span className="bp-brand">
          <i aria-hidden="true" />
          <span className="bp-brand-name">{nav.companyName ?? nav.tenant}</span>
        </span>
        {/* where the places don't fit (a phone) they scroll sideways, and the edge they go on past fades
            out, so the last one showing reads as more, never as a word cut short (QA-841) */}
        <nav className="bp-places" aria-label="Places" ref={watchScrollEdges}>
          {places.map((p) => {
            const pages = pagesOf(p);
            // a place opens on its first page with work waiting (QA-344), else its first page; Schedule
            // always opens on the day board, the dispatcher's nerve centre
            const first =
              (p.opensOnFirst ? undefined : pages.find((x) => waitingOn(nav, x.view, x.part) > 0)) ??
              pages[0]!;
            return (
              <a
                key={p.id}
                href={href(first)}
                aria-current={p.id === here?.id ? 'page' : undefined}
                {...described(countedOf(p))}
                onClick={p.id === here?.id ? go(nav.view, nav.part ?? undefined) : go(first.view, first.part)}
              >
                {p.name}
                <Count n={waits(countedOf(p))} hot={ringing(countedOf(p))} />
              </a>
            );
          })}
        </nav>
        <div className="bp-tools">
          {/* the search across the top, on every page (BL-134) */}
          {maySearch(held) && <HeaderSearch />}
          {nav.notes && nav.onOpenNote && (
            <NotificationsBell
              notes={nav.notes}
              {...(nav.timeZone ? { timeZone: nav.timeZone } : {})}
              onOpen={nav.onOpenNote}
            />
          )}
          <button type="button" aria-haspopup="dialog" onClick={() => setHelp(true)}>
            Help
          </button>
          <button
            type="button"
            onClick={() => {
              forgetSessionCaches();
              void nav.onSignOut();
            }}
          >
            Sign out
          </button>
          {/* the person's own menu: Your sign-in, and light or dark (BL-135) */}
          <MeMenu current={nav.view === 'signin'} onSignIn={() => nav.onView('signin')} />
        </div>
        {waiting.map((v) => (
          <span key={v} id={`bp-waiting-${v}`} hidden>
            {waitingWords(v, waitingOn(nav, v))}
          </span>
        ))}
      </header>
      {strip}
      <div className="bp-frame">
        {here && (
          <nav className="bp-rail" aria-label={here.name} ref={watchScrollEdges}>
            <div className="bp-rail-head">{here.name}</div>
            {here.groups.map((g) => (
              <section key={g.name} className="bp-rail-grp">
                <h2>{g.name}</h2>
                {g.pages.map((x) => (
                  <a
                    key={href(x)}
                    href={href(x)}
                    aria-current={isOpen(x, nav.view, nav.part) ? 'page' : undefined}
                    {...described([x])}
                    onClick={go(x.view, x.part)}
                  >
                    <span className="bp-rail-lbl">{x.label}</span>
                    <span className="bp-rail-lead" aria-hidden="true" />
                    <Count n={waitingOn(nav, x.view, x.part)} hot={x.view === 'calls' && nav.ringing > 0} />
                  </a>
                ))}
              </section>
            ))}
          </nav>
        )}
        {/* the screen's crumb: the place, its first page this person opens, and the page (Nav.tsx's Header) */}
        <CrumbContext.Provider
          value={{
            // the place as this person's top bar names it (QA-864: the warehouse's Stock)
            place: (here?.id === at.id ? here.name : null) ?? at.name,
            first: (at.id === here?.id ? here.groups[0]?.pages[0]?.view : null) ?? null,
            firstPart: (at.id === here?.id ? here.groups[0]?.pages[0]?.part : null) ?? null,
            page: pageLabel(nav.view, nav.part),
          }}
        >
          <div className="bp-sheet">{children}</div>
        </CrumbContext.Provider>
      </div>
      {help && (
        <HelpDialog
          held={nav.permissions ?? []}
          {...(nav.timeZone ? { timeZone: nav.timeZone } : {})}
          onClose={() => setHelp(false)}
        />
      )}
    </div>
  );
}
